import styled from "styled-components";

import { WHITE, GRAY_50, GRAY_100, GRAY_200, GRAY_500, GRAY_700, GRAY_900, PRIMARY_600 } from "../../constant/colors";

/**
 * Visual styling for the agentic dashboard — KPI tiles, section cards, the
 * funnel bars, and the outcome donut. Shares the Tasks-hub's neutral card look
 * (white card, thin gray border, soft shadow) so the two agentic pages read as
 * one product.
 */

export const DashboardContainer = styled.div`
  width: 98%;
  margin: 0 auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// One card per dashboard section (funnel / outcomes / failed panel).
export const Card = styled.div`
  background: ${WHITE};
  border: 1px solid ${GRAY_200};
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.08);
  padding: 18px 20px;
`;

export const CardTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${GRAY_900};
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const CardCaption = styled.div`
  font-size: 12px;
  color: ${GRAY_500};
  margin-top: 2px;
  margin-bottom: 14px;
`;

// KPI tiles — a responsive auto-fit grid so the tiles wrap cleanly on
// narrow widths instead of overflowing.
export const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
`;

export const KpiTile = styled.div`
  background: ${WHITE};
  border: 1px solid ${GRAY_200};
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.08);
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 6px;
`;

export const KpiLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${GRAY_500};
  text-transform: uppercase;
  letter-spacing: 0.03em;
  line-height: 1.3;
  /* Reserve two lines so a label that wraps (e.g. "Waiting — consent approval")
     doesn't push its value down — keeps every big number on the same baseline
     across the row. */
  min-height: 2.6em;
`;

export const KpiValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.1;
  color: ${(p) => p.$color || GRAY_900};
`;

export const KpiSub = styled.div`
  font-size: 12px;
  color: ${GRAY_500};
`;

// Always-visible per-queue breakdown under the "Waiting for human" total —
// label (left) + count (right), one row per task. Full width + left/right
// aligned, overriding the tile's centered layout.
export const KpiBreakdown = styled.div`
  width: 100%;
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px solid ${GRAY_100};
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

export const KpiBreakdownRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 12px;
  color: ${GRAY_500};
  & b {
    color: ${GRAY_900};
    font-weight: 600;
  }
`;

// A two-column layout for funnel (wider) + outcomes; stacks under 900px.
export const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 16px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

// ─── Funnel ────────────────────────────────────────────────────────────────
export const FunnelRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 14px;
  &:last-child {
    margin-bottom: 0;
  }
`;

export const FunnelHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

export const FunnelLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${GRAY_700};
`;

export const FunnelMeta = styled.span`
  font-size: 12px;
  color: ${GRAY_500};
  & b {
    color: ${GRAY_900};
    font-weight: 600;
  }
`;

export const FunnelTrack = styled.div`
  height: 22px;
  background: ${GRAY_100};
  border-radius: 6px;
  overflow: hidden;
`;

export const FunnelFill = styled.div`
  height: 100%;
  width: ${(p) => p.$pct}%;
  min-width: ${(p) => (p.$pct > 0 ? "2px" : "0")};
  background: ${PRIMARY_600};
  border-radius: 6px;
  transition: width 0.3s ease;
`;

export const DropOff = styled.span`
  font-size: 11px;
  color: ${GRAY_500};
`;

// ─── Outcome breakdown (donut + legend) ──────────────────────────────────────
// The card lays the donut beside its legend; wraps to stacked on a narrow card.
export const OutcomeDonutWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  padding-top: 6px;
`;

export const OutcomeDonut = styled.div`
  position: relative;
  flex: 0 0 auto;
  width: 150px;
  height: 150px;
`;

// Centered total inside the donut hole.
export const DonutCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

export const DonutTotal = styled.div`
  font-size: 24px;
  font-weight: 700;
  line-height: 1;
  color: ${GRAY_900};
`;

export const DonutCenterLabel = styled.div`
  font-size: 11px;
  color: ${GRAY_500};
  margin-top: 2px;
`;

export const OutcomeLegend = styled.div`
  flex: 1 1 200px;
  min-width: 0;
`;

// One legend row per outcome (the donut's key): tone dot + label, count + share.
export const OutcomeRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 8px 12px;
  padding: 6px 0;
  & + & {
    border-top: 1px solid ${GRAY_50};
  }
`;

export const OutcomeLabel = styled.span`
  font-size: 13px;
  color: ${GRAY_700};
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ToneDot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  flex: 0 0 auto;
  background: ${(p) => p.$color};
`;

export const OutcomeCount = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${GRAY_900};
  text-align: right;
  white-space: nowrap;
  & span {
    font-weight: 400;
    color: ${GRAY_500};
    margin-left: 6px;
  }
`;
